import type {
  Org,
  OrgMember,
  OrgMemberRole,
  OrgCapability,
  ServiceProvider,
  TeamService,
  Vendor,
} from '@shopgnd/shared';
import { capabilitiesFromBusinessModes, deriveCapabilitiesFromVendor } from '@/lib/orgCapabilities';
import { getAuthedDataClient, getDataClient, getCatalogClient } from '@/lib/amplify-client';
import { callTeamServiceAction } from '@/api/teamServices';
import { hubQueryClient, hubQueryKeys } from '@/lib/hubQueryClient';

function mapOrg(row: Record<string, unknown>): Org {
  return {
    id: String(row.id),
    ownerId: String(row.ownerId),
    name: String(row.name),
    slug: String(row.slug),
    capabilities: (row.capabilities as OrgCapability[]) ?? [],
    legacyVendorId: row.legacyVendorId ? String(row.legacyVendorId) : undefined,
    legacyProId: row.legacyProId ? String(row.legacyProId) : undefined,
    description: row.description ? String(row.description) : undefined,
    logoUrl: row.logoUrl ? String(row.logoUrl) : undefined,
    phone: row.phone ? String(row.phone) : undefined,
    directorySector: row.directorySector as Org['directorySector'],
    directoryCategoryId: row.directoryCategoryId ? String(row.directoryCategoryId) : undefined,
    businessModes: row.businessModes as Org['businessModes'],
    primaryMode: row.primaryMode as Org['primaryMode'],
    subscriptionPlanId: row.subscriptionPlanId ? String(row.subscriptionPlanId) : undefined,
    subscriptionStatus: row.subscriptionStatus ? String(row.subscriptionStatus) : undefined,
    subscriptionTrialEndsAt: row.subscriptionTrialEndsAt ? String(row.subscriptionTrialEndsAt) : undefined,
    stripeCustomerId: row.stripeCustomerId ? String(row.stripeCustomerId) : undefined,
    stripeSubscriptionId: row.stripeSubscriptionId ? String(row.stripeSubscriptionId) : undefined,
    subscriptionWaived: Boolean(row.subscriptionWaived),
    createdAt: row.createdAt ? String(row.createdAt) : undefined,
    updatedAt: row.updatedAt ? String(row.updatedAt) : undefined,
  };
}

function mapMember(row: Record<string, unknown>): OrgMember {
  return {
    id: String(row.id),
    orgId: String(row.orgId),
    ownerUserId: String(row.ownerUserId),
    userId: String(row.userId),
    role: row.role as OrgMemberRole,
    isBookable: Boolean(row.isBookable),
    displayName: row.displayName ? String(row.displayName) : undefined,
    bio: row.bio ? String(row.bio) : undefined,
    avatarUrl: row.avatarUrl ? String(row.avatarUrl) : undefined,
    inviteEmail: row.inviteEmail ? String(row.inviteEmail) : undefined,
    inviteToken: row.inviteToken ? String(row.inviteToken) : undefined,
    inviteStatus: row.inviteStatus as OrgMember['inviteStatus'],
    createdAt: row.createdAt ? String(row.createdAt) : undefined,
    updatedAt: row.updatedAt ? String(row.updatedAt) : undefined,
  };
}

export type WorkspaceContext = {
  org: Org;
  member: OrgMember;
};

export async function findOrgByLegacyVendorId(vendorId: string): Promise<Org | null> {
  try {
    const client = await getCatalogClient();
    if (!client.models?.Org?.list) return null;
    const res = await client.models.Org.list({
      filter: { legacyVendorId: { eq: vendorId } },
      limit: 1,
    });
    const row = res.data?.[0] as Record<string, unknown> | undefined;
    return row ? mapOrg(row) : null;
  } catch {
    return null;
  }
}

export async function findOrgByLegacyProId(proId: string): Promise<Org | null> {
  try {
    const client = await getCatalogClient();
    if (!client.models?.Org?.list) return null;
    const res = await client.models.Org.list({
      filter: { legacyProId: { eq: proId } },
      limit: 1,
    });
    const row = res.data?.[0] as Record<string, unknown> | undefined;
    return row ? mapOrg(row) : null;
  } catch {
    return null;
  }
}

/** Public storefront: resolve workspace org id from vendor profile. */
export async function resolveOrgIdForVendor(vendorId: string): Promise<string | null> {
  const org = await findOrgByLegacyVendorId(vendorId);
  return org?.id ?? null;
}

async function createOwnerMember(
  orgId: string,
  ownerUserId: string,
  displayName: string,
  isBookable: boolean,
): Promise<OrgMember> {
  const client = await getAuthedDataClient();
  const res = await client.models.OrgMember.create({
    orgId,
    ownerUserId,
    userId: ownerUserId,
    role: 'owner',
    isBookable,
    displayName,
    inviteStatus: 'accepted',
  });
  if (!res.data) throw new Error('Could not create organization owner membership.');
  return mapMember(res.data as Record<string, unknown>);
}

async function findOrgByOwnerId(ownerId: string): Promise<Org | null> {
  try {
    const client = await getCatalogClient();
    if (!client.models?.Org?.list) return null;
    const res = await client.models.Org.list({
      filter: { ownerId: { eq: ownerId } },
      limit: 1,
    });
    const row = res.data?.[0] as Record<string, unknown> | undefined;
    return row ? mapOrg(row) : null;
  } catch {
    return null;
  }
}

async function createVendorOrgRecord(
  client: Awaited<ReturnType<typeof getAuthedDataClient>>,
  vendor: Vendor,
  capabilities: OrgCapability[],
): Promise<Org> {
  const base = {
    ownerId: vendor.ownerUserId,
    name: vendor.businessName,
    capabilities,
    legacyVendorId: vendor.id,
    legacyProId: vendor.linkedProId,
    logoUrl: vendor.logoUrl,
    phone: vendor.phone,
    directorySector: vendor.directorySector,
    directoryCategoryId: vendor.directoryCategoryId,
    businessModes: vendor.businessModes,
    primaryMode: vendor.primaryMode,
    subscriptionPlanId: vendor.subscriptionPlanId,
    subscriptionStatus: vendor.subscriptionStatus,
    subscriptionTrialEndsAt: vendor.subscriptionTrialEndsAt,
    stripeCustomerId: vendor.stripeCustomerId,
    stripeSubscriptionId: vendor.stripeSubscriptionId,
    subscriptionWaived: vendor.subscriptionWaived,
  };

  const slugCandidates = [
    vendor.slug,
    `${vendor.slug}-${vendor.id.slice(-6)}`,
    `org-${vendor.id.slice(-8)}`,
  ];

  for (const slug of slugCandidates) {
    try {
      const orgRes = await client.models.Org.create({ ...base, slug });
      if (orgRes.data) return mapOrg(orgRes.data as Record<string, unknown>);
    } catch (e) {
      console.warn('[ShopGND] Org.create failed for slug', slug, e);
    }
  }
  throw new Error('Could not create organization workspace.');
}

async function repairOwnerMembership(
  org: Org,
  ownerUserId: string,
  displayName: string,
  isBookable: boolean,
): Promise<WorkspaceContext> {
  const workspace = await loadWorkspaceForUser(ownerUserId);
  if (workspace) return workspace;
  if (org.ownerId !== ownerUserId) {
    throw new Error('Store is already linked to another organization.');
  }
  const member = await createOwnerMember(org.id, ownerUserId, displayName, isBookable);
  return { org, member };
}

export async function createOrgForVendor(vendor: Vendor): Promise<WorkspaceContext> {
  // Prefer hub-api ensureOrg when available (Org-first path with projection sync).
  try {
    const { hubEnsureOrg, isHubApiConfigured } = await import('@/api/hub');
    if (await isHubApiConfigured()) {
      const caps = deriveCapabilitiesFromVendor(vendor);
      const ensured = await hubEnsureOrg({
        name: vendor.businessName,
        slug: vendor.slug,
        capabilities: caps,
        legacyVendorId: vendor.id,
        legacyProId: vendor.linkedProId,
        description: vendor.description,
        phone: vendor.phone,
        subscriptionPlanId: vendor.subscriptionPlanId,
        businessModes: vendor.businessModes as string[] | undefined,
        primaryMode: vendor.primaryMode,
      });
      const workspace = await loadWorkspaceForUser(vendor.ownerUserId);
      if (workspace) return workspace;
      if (ensured.orgId) {
        const byId = await findOrgByLegacyVendorId(vendor.id);
        if (byId) {
          return repairOwnerMembership(
            byId,
            vendor.ownerUserId,
            vendor.businessName,
            Boolean(vendor.linkedProId),
          );
        }
      }
    }
  } catch (e) {
    console.warn('[ShopGND] hubEnsureOrg failed, falling back to AppSync Org create', e);
  }

  const existing =
    (await findOrgByLegacyVendorId(vendor.id)) ??
    (await findOrgByOwnerId(vendor.ownerUserId));
  if (existing) {
    const client = await getAuthedDataClient();
    if (!existing.legacyVendorId) {
      try {
        const linked = await client.models.Org.update({
          id: existing.id,
          legacyVendorId: vendor.id,
          legacyProId: vendor.linkedProId ?? existing.legacyProId,
        });
        if (linked.data) {
          return repairOwnerMembership(
            mapOrg(linked.data as Record<string, unknown>),
            vendor.ownerUserId,
            vendor.businessName,
            Boolean(vendor.linkedProId),
          );
        }
      } catch (e) {
        console.warn('[ShopGND] Org.update legacyVendorId failed', e);
      }
    }
    return repairOwnerMembership(
      existing,
      vendor.ownerUserId,
      vendor.businessName,
      Boolean(vendor.linkedProId),
    );
  }

  const capabilities = deriveCapabilitiesFromVendor(vendor);
  const client = await getAuthedDataClient();
  const org = await createVendorOrgRecord(client, vendor, capabilities);
  const member = await createOwnerMember(
    org.id,
    vendor.ownerUserId,
    vendor.businessName,
    Boolean(vendor.linkedProId),
  );
  return { org, member };
}

export async function linkProToOwnerOrg(pro: ServiceProvider): Promise<WorkspaceContext | null> {
  const client = await getAuthedDataClient();
  const byPro = await findOrgByLegacyProId(pro.id);
  if (byPro) {
    const workspace = await loadWorkspaceForUser(pro.ownerUserId);
    if (workspace) return workspace;
  }

  const vendorOrg = await loadWorkspaceForUser(pro.ownerUserId);
  if (vendorOrg) {
    const caps = new Set(vendorOrg.org.capabilities);
    caps.add('bookings');
    caps.add('classifieds');
    const res = await client.models.Org.update({
      id: vendorOrg.org.id,
      legacyProId: pro.id,
      capabilities: [...caps],
    });
    if (res.data) {
      return {
        org: mapOrg(res.data as Record<string, unknown>),
        member: vendorOrg.member,
      };
    }
    return vendorOrg;
  }

  const orgRes = await client.models.Org.create({
    ownerId: pro.ownerUserId,
    name:
      pro.providerType === 'company' && pro.companyName
        ? pro.companyName
        : pro.headline.slice(0, 80),
    slug: pro.id.slice(0, 48),
    capabilities: ['bookings', 'classifieds'],
    legacyProId: pro.id,
  });
  if (!orgRes.data) throw new Error('Could not create organization workspace.');
  const org = mapOrg(orgRes.data as Record<string, unknown>);
  const member = await createOwnerMember(org.id, pro.ownerUserId, pro.headline, true);
  return { org, member };
}

export async function createOrgForPro(pro: ServiceProvider): Promise<WorkspaceContext> {
  const linked = await linkProToOwnerOrg(pro);
  if (linked) return linked;
  throw new Error('Could not link service pro to workspace.');
}

export async function loadWorkspaceForUser(userId: string): Promise<WorkspaceContext | null> {
  try {
    const client = await getDataClient();
    if (!client.models?.OrgMember?.list) return null;

    const memberRes = await client.models.OrgMember.list({
      filter: { userId: { eq: userId } },
    });
    const memberRow = memberRes.data?.[0] as Record<string, unknown> | undefined;
    if (!memberRow) return null;

    const orgRes = await client.models.Org.get({ id: String(memberRow.orgId) });
    const orgRow = orgRes.data as Record<string, unknown> | undefined;
    if (!orgRow) return null;

    return { org: mapOrg(orgRow), member: mapMember(memberRow) };
  } catch {
    return null;
  }
}

/**
 * Load persisted workspace or create Org + owner member from legacy Vendor/Pro rows.
 */
export async function ensureWorkspaceForUser(
  userId: string,
  legacy: { vendor?: Vendor | null; pro?: ServiceProvider | null },
): Promise<WorkspaceContext | null> {
  const existing = await loadWorkspaceForUser(userId);
  if (existing) return existing;

  if (legacy.vendor) {
    return createOrgForVendor(legacy.vendor);
  }
  if (legacy.pro) {
    return createOrgForPro(legacy.pro);
  }
  return null;
}

export function invalidateWorkspaceQuery(userId: string) {
  void hubQueryClient.invalidateQueries({ queryKey: ['hub', 'workspace', userId] });
}

export async function listOrgMembers(orgId: string): Promise<OrgMember[]> {
  try {
    const data = (await callTeamServiceAction({
      action: 'listOrgMembers',
      orgId,
    })) as { members?: Record<string, unknown>[] };
    return (data.members ?? []).map((row) => mapMember(row));
  } catch (e) {
    console.warn('[ShopGND] listOrgMembers via team-service failed', e);
    return [];
  }
}

export async function getOrgMemberByInviteToken(
  token: string,
): Promise<{ member: OrgMember; org: Org } | null> {
  try {
    const data = (await callTeamServiceAction(
      {
        action: 'resolveInvite',
        inviteToken: token.trim(),
      },
      { requireAuth: false },
    )) as { member?: Record<string, unknown>; org?: Record<string, unknown> };
    if (!data.member || !data.org) return null;
    return {
      member: mapMember(data.member),
      org: {
        id: String(data.org.id),
        ownerId: '',
        name: String(data.org.name),
        slug: '',
        capabilities: [],
      },
    };
  } catch {
    return null;
  }
}

export async function acceptOrgInvite(input: {
  memberId: string;
  userId: string;
}): Promise<void> {
  const client = await getDataClient();
  if (!client.models?.OrgMember?.update) throw new Error('Org member updates unavailable.');

  await client.models.OrgMember.update({
    id: input.memberId,
    userId: input.userId,
    inviteStatus: 'accepted',
    inviteToken: null,
  });
}

export async function listTeamServices(orgId: string): Promise<TeamService[]> {
  const client = await getDataClient();
  if (!client.models?.TeamService?.list) return [];

  const res = await client.models.TeamService.list({
    filter: { orgId: { eq: orgId } },
  });
  return (res.data ?? []).map((row: Record<string, unknown>) => ({
    id: String(row.id),
    orgId: String(row.orgId),
    ownerUserId: String(row.ownerUserId),
    memberId: row.memberId ? String(row.memberId) : null,
    name: String(row.name),
    description: row.description ? String(row.description) : undefined,
    durationMin: Number(row.durationMin),
    priceCents: Number(row.priceCents),
    category: row.category ? String(row.category) : undefined,
    isActive: row.isActive !== false,
    legacyOfferingId: row.legacyOfferingId ? String(row.legacyOfferingId) : undefined,
  }));
}

/** Re-export for callers that need capability derivation from modes only. */
export { capabilitiesFromBusinessModes };
